import { apiFetch } from '../apiFetch'

export type GroupByPeriod = 'day' | 'week' | 'month'
export type OutputReportView = 'by_period' | 'order_parts' | 'order_cumulative'

export type StageName =
  | 'PHOI'
  | 'DINH_HINH'
  | 'LAP_RAP'
  | 'CHA_NHAM'
  | 'SON'
  | 'FITTING'
  | 'DONG_GOI'

export const STAGE_DISPLAY_NAMES: Record<StageName, string> = {
  PHOI: 'Phôi',
  DINH_HINH: 'Định hình',
  LAP_RAP: 'Lắp ráp',
  CHA_NHAM: 'Chà nhám',
  SON: 'Sơn',
  FITTING: 'Fitting',
  DONG_GOI: 'Đóng gói',
}

export type ProductionOutputPeriodRow = {
  period_key: string
  period_label: string
  plan_line_id: string
  order_no: string
  product_code: string
  product_name: string
  variant_name: string | null
  customer_name: string | null
  phoi_qty: number
  dinh_hinh_qty: number
  lap_rap_qty: number
  cha_nham_qty: number
  son_qty: number
  fitting_qty: number
  dong_goi_qty: number
}

export type ProductionOrderPartRow = {
  part_snapshot_id: string
  stt: number
  part_name: string
  part_group: string | null
  spec: string
  wood_type: string | null
  qty_per: number
  target_qty: number
  phoi_in_period: number
  dinh_hinh_in_period: number
  total_in_period: number
  cum_phoi: number
  cum_dinh_hinh: number
  total_cum: number
  wip_waiting_assembly: number
  progress_pct: number
}

export type ProductionOrderPartsReport = {
  plan_line_id: string
  order_no: string
  product_code: string
  product_name: string
  variant_name: string | null
  customer_name: string | null
  planned_qty: number
  requested_ship_date: string | null
  parts: ProductionOrderPartRow[]
  summary: {
    total_qty_per: number
    total_target_qty: number
    total_phoi_in_period: number
    total_dinh_hinh_in_period: number
    total_cum_phoi: number
    total_cum_dinh_hinh: number
    total_wip_waiting: number
  }
}

export type ProductionOrderCumulativeRow = {
  stt: number
  plan_line_id: string
  order_no: string
  product_code: string
  product_name: string
  variant_name: string | null
  customer_name: string | null
  planned_qty: number
  requested_ship_date: string | null
  cum_phoi: number
  cum_dinh_hinh: number
  cum_lap_rap: number
  cum_cha_nham: number
  cum_son: number
  cum_fitting: number
  cum_dong_goi: number
  remaining_qty: number
  progress_pct: number
  latest_event_date: string | null
  ship_status: {
    label: string
    code: 'done' | 'on_track' | 'at_risk' | 'overdue' | 'no_deadline'
    days_diff: number | null
  }
}

export type ProductionOutputSummary = {
  total_phoi: number
  total_dinh_hinh: number
  total_lap_rap: number
  total_cha_nham: number
  total_son: number
  total_fitting: number
  total_dong_goi: number
}

export type AvailablePlanLineOption = {
  plan_line_id: string
  order_no: string
  product_code: string
  product_name: string
  customer_name: string | null
}

export type ProductionOutputReportData = {
  filters: {
    from: string
    to: string
    group_by: GroupByPeriod
    plan_line_id?: string
    product_code?: string
    stage?: StageName
    view: OutputReportView
  }
  by_period: ProductionOutputPeriodRow[]
  order_parts: ProductionOrderPartsReport | null
  order_cumulative: ProductionOrderCumulativeRow[]
  summary: ProductionOutputSummary
  available_orders: AvailablePlanLineOption[]
  pagination: {
    page: number
    limit: number
    total: number
    total_pages: number
  }
}

export type ProductionOutputReportParams = {
  from?: string
  to?: string
  group_by?: GroupByPeriod
  plan_line_id?: string
  product_code?: string
  stage?: StageName
  view?: OutputReportView
  page?: number
  limit?: number
}

export async function fetchProductionOutputReportApi(
  params: ProductionOutputReportParams = {},
): Promise<ProductionOutputReportData> {
  const qs = new URLSearchParams()
  if (params.from) qs.set('from', params.from)
  if (params.to) qs.set('to', params.to)
  if (params.group_by) qs.set('group_by', params.group_by)
  if (params.plan_line_id) qs.set('plan_line_id', params.plan_line_id)
  if (params.product_code) qs.set('product_code', params.product_code)
  if (params.stage) qs.set('stage', params.stage)
  if (params.view) qs.set('view', params.view)
  if (params.page != null) qs.set('page', String(params.page))
  if (params.limit != null) qs.set('limit', String(params.limit))

  const suffix = qs.toString()
  const res = await apiFetch(`/api/reports/production-output${suffix ? `?${suffix}` : ''}`)

  if (!res.ok) {
    let message = `Không thể tải báo cáo sản lượng (${res.status})`
    try {
      const body = (await res.json()) as { error?: { message?: string } }
      if (body?.error?.message) message = body.error.message
    } catch {
      // ignore
    }
    throw new Error(message)
  }

  const json = (await res.json()) as { ok: boolean; data: ProductionOutputReportData }
  return json.data
}
